import type { ChainMode } from './chainMode'

/* `cls` pairs each label with the cells under it — see `GreeksCell`. A
   heading that outlives the column it names is how a theta gets read as a
   vega, so the two carry the same hook and are hidden by the same rule. */
const RISKS = [
  { cls: 'tr-h-gam', label: 'Γ', title: 'Gamma — delta change per point of the underlying.' },
  { cls: 'tr-h-the', label: 'Θ/day', title: 'Theta — option value change per calendar day.' },
  { cls: 'tr-h-veg', label: 'ν/1%', title: 'Vega — option value change per one percentage-point increase in IV.' },
  { cls: 'tr-h-rho', label: 'ρ/1%', title: 'Rho — option value change per one percentage-point increase in the interest rate.' },
] as const

/**
 * The two header rows: the bands that say which half you are reading, and
 * the column labels under them.
 *
 * The bands are the orientation, so COMBINED mode gets a third one rather
 * than hanging its columns off the puts' band — a label that does not sit
 * over the columns it names is worse than none.
 */
export function ChainHeader({
  mode,
  expiry,
  onRefresh,
  showOi = false,
}: {
  mode: ChainMode
  expiry: string
  onRefresh: () => void
  /*
   * Whether the ROWS are drawing open interest under the price.
   *
   * Passed in rather than derived from `mode`, because COMBINED draws it only
   * with the whole window — and a header that names a column the rows are not
   * drawing is worse than an unlabelled one. Same flag, same source, so the
   * two cannot disagree.
   */
  showOi?: boolean
}) {
  const risks = (side: 'ce' | 'pe') => (side === 'ce' ? RISKS : [...RISKS].reverse()).map((metric) => (
    <span key={metric.label} className={`tr-hcell ${metric.cls} ${side === 'ce' ? 'justify-end' : ''}`} title={metric.title}>{metric.label}</span>
  ))
  const greeks = mode === 'GREEKS'
  const combined = mode === 'COMBINED'
  return <div className="tr-thead">
    <div className="tr-grid">
      {/* Classed by band so a container query can drop the wings in
          COMBINED mode without counting children — see `tr-combined` in
          terminalRedesign.css. */}
      <div className="tr-grp tr-band-ce">Calls</div>
      <div className="tr-grp tr-band-strike">Strike</div>
      <div className="tr-grp tr-band-pe">Puts</div>
      {combined && <div className="tr-grp tr-band-cmb" title="CE LTP + PE LTP">Combined premium</div>}
    </div>
    <div className="tr-grid border-b border-border">
      <div className="tr-side ce">
        {greeks ? risks('ce') : combined ? null : <span className="tr-hcell tr-h-itm justify-end" title="Model probability of expiring in the money">ITM%</span>}
        <span className="tr-hcell justify-end" title="Delta — option value change per point of the underlying">Δ</span>
        <span className="tr-hcell tr-h-iv justify-end"><button type="button" onClick={onRefresh} title="Refresh model IV and Greeks" data-testid="iv-refresh">IV</button></span>
        <span className="tr-hcell justify-end" title={showOi ? 'Last traded price, with open interest underneath' : 'Last traded price'}>{showOi ? 'LTP \u00b7 OI' : 'LTP'}</span><span className="tr-hcell" />
      </div>
      <div className="tr-hcell tr-band-strike justify-center text-[9.5px]">{expiry}</div>
      <div className="tr-side pe">
        <span className="tr-hcell" /><span className="tr-hcell" title={showOi ? 'Last traded price, with open interest underneath' : 'Last traded price'}>{showOi ? 'LTP \u00b7 OI' : 'LTP'}</span><span className="tr-hcell tr-h-iv">IV</span>
        <span className="tr-hcell" title="Delta — option value change per point of the underlying">Δ</span>
        {greeks ? risks('pe') : combined ? null : <span className="tr-hcell tr-h-itm" title="Model probability of expiring in the money">ITM%</span>}
      </div>
      {combined && (
        <div className="tr-side cmb">
          <span className="tr-hcell justify-end" title="Call LTP + put LTP — premium per unit">Total</span>
          <span className="tr-hcell justify-end" title="The call's share of the combined premium.">CE %</span>
          <span className="tr-hcell justify-end" title="The put's share of the combined premium. The two say where the money is without reading the spot.">PE %</span>
          <span className="tr-hcell" />
        </div>
      )}
    </div>
  </div>
}
